import React from 'react'
import { useHistory } from 'react-router-dom'

import { customFolder } from '../../../../../constants/constants'
import { RoutePath } from '../../../../../router/routesConfig'

import useLookupService from '../../../../../services/lookup/useLookupService'
import useWebService from '../../../../../services/web/useWebService'
import useLoading from '../../../../../control/loading/useLoading'
import useManageProjectAction from '../../../../../stores/manageProject/useManageProjectAction'
import useManageProjectStore from '../../../../../stores/manageProject/useManageProjectStore'
import useAppFolderAndTabAction from '../../../../../stores/appFolderAndTab/appFolderAndTabAction'
import useFolderAndTabStore from '../../../../../stores/appFolderAndTab/appFolderAndTabStore'
import useFolderAndTab from '../../useFolderAndTab'

import useTranslate from '../../../../../i18n/useTranslate'
import checkPermission from '../../../../../utilities/checkPermission'

import * as confirmProjectWithdrawType from './confirmProjectWithdrawType'
import { DialogType } from '../../../../../control/dialog/dialog'
import constSubmittedType from '../../../../../constants/submittedType'
import * as REQServiceTypes from '../../../../../services/web/types/requestWebServiceTypes'
import * as RESServiceTypes from '../../../../../services/web/types/responseWebServiceTypes'
import { responseWebServiceTypes } from '../../../../../services/web/types'
import * as DateUtils from '../../../../../utilities/date'

import { ConfirmProjectWithdrawProps } from './confirmProjectWithdraw'

const useConfirmProjectWithdraw = (props: ConfirmProjectWithdrawProps) => {
    const { onSaveComplete } = props
    const [initialValues, setInitialValues] = React.useState<any>({})
    const [initialUploadFile, setInitialUploadFile] = React.useState<any>()
    const [projectPageData, setProjectPageData] = React.useState<RESServiceTypes.GetProjectWithdrawResponse>()
    const [configDialog, setConfigDialog] =
        React.useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()

    const formRef = React.useRef<any>()
    const dialogRef = React.useRef<Record<string, Function>>()

    const loadingHook = useLoading()
    const callLookup = useLookupService()
    const callWebService = useWebService()
    const history = useHistory()
    const translate = useTranslate()
    const checkPermissionHook = checkPermission()
    const tabAndFolderHook = useFolderAndTab()
    const folderAndTabStore = useFolderAndTabStore()
    const appFolderAndTabAction = useAppFolderAndTabAction()
    const manageProjectAction = useManageProjectAction()
    const manageProjectStore = useManageProjectStore()
    const { folderSelected } = folderAndTabStore
    const isViewMode = checkPermissionHook.chkProjectStepViewMode(manageProjectStore.projectData)

    React.useEffect(() => {
        if (manageProjectStore.projectData) {
            console.log('####Page: Project Withdraw####')
            loadProjectPageData()
        }
    }, [manageProjectStore.projectData?.submittedID])

    const getDocumentTopicLut = async (irb: number, submitTypeID: number) => {
        const res = await callLookup.callDocumentTopicLut(irb, submitTypeID)
        if (res.status === 1) {
            return res.result
        } else {
            return []
        }
    }

    const getSaveServiceParameter = (isDraft: boolean) => {
        const values = formRef.current?.values
        let reviewerUserID = null
        if (values?.reviewer?.value) {
            reviewerUserID = parseInt(values.reviewer.value)
        }
        const param: REQServiceTypes.SaveVerifyProjectWithdrawType = {
            projectID: manageProjectStore.projectData?.projectID ?? null,
            submittedID: manageProjectStore.projectData?.submittedID ?? null,
            isCompletely: values?.isCompletely ?? false,
            resubmitDocDate: DateUtils.dateForAPI(values?.resubmitDocDate) ?? null,
            verifyDocRemark: values?.remark ?? null,
            isDraft: isDraft,
            reviewerUserID: reviewerUserID,
        }
        return param
    }

    const loadProjectPageData = async () => {
        if (manageProjectStore.projectData?.submittedID) {
            initialProcessUpdate()
        } else {
            initialProcessCreate()
        }
    }

    const initialProcessCreate = () => {
        createInitialData()
        initialUpload()
    }

    const initialProcessUpdate = async () => {
        if (manageProjectStore.projectData?.submittedID) {
            const res = await callWebService.getProjectWithdraw(manageProjectStore.projectData.submittedID)
            if (res.status === 1) {
                setProjectPageData(res.result)

                createInitialData(res.result)
                initialUpload(res.result.documentInfo)
            } else {
                setProjectPageData(undefined)
            }
        }
    }

    const createInitialData = (initVal?: responseWebServiceTypes.GetProjectWithdrawResponse) => {
        const initialData: Record<string, any> = {
            withdrawCause: null,
            isCompletelyDoc: '1',
            isCompletely: true,
            resubmitDocDate: null,
            remark: null,
        }
        if (initVal) {
            initialData.withdrawCause = initVal.reason
            initialData.isCompletelyDoc =
                initVal.isVerifyDocumentCompleted !== null ? (initVal.isVerifyDocumentCompleted ? '1' : '2') : null

            initialData.isCompletely = initVal.isVerifyDocumentCompleted
            initialData.remark = initVal.verifyDocRemark
            initialData.resubmitDocDate = initVal.resubmittedDate ? new Date(initVal.resubmittedDate) : null
        }

        setInitialValues(initialData)
    }

    const initialUpload = async (
        initDocumentInfo?: responseWebServiceTypes.GetProjectWithdrawResponse['documentInfo'],
    ) => {
        if (manageProjectStore.projectData?.irb) {
            const lstDocumentTopic = await getDocumentTopicLut(
                manageProjectStore.projectData?.irb,
                constSubmittedType.withdrawalProject,
            )
            manageProjectAction.setDocumentTopic(lstDocumentTopic)

            let uploadFileInit = lstDocumentTopic?.map((itm: any, idx: number) => {
                return {
                    uploadFileObj: {
                        fileList: [],
                        subTopicPageList: [],
                        documentTopicID: itm.id,
                        isDisplay: itm.isDisplay,
                        isRequired: itm.isRequired,
                    },
                }
            }) as Array<confirmProjectWithdrawType.UploadFileObjType>

            if (folderSelected.id !== customFolder.withdrawProject.id) {
                uploadFileInit?.forEach((itm: confirmProjectWithdrawType.UploadFileObjType) => {
                    initDocumentInfo?.forEach((docInfo) => {
                        if (docInfo.topicID === itm.uploadFileObj.documentTopicID) {
                            if (docInfo.fileInfo?.length > 0) {
                                //@ts-ignore
                                itm.uploadFileObj.fileList.push({
                                    ...docInfo.fileInfo[0],
                                    fileID: docInfo.fileInfo[0].fileToken,
                                    documentTopicID: docInfo.fileInfo[0].topicID as number,
                                    fileToken: docInfo.fileInfo[0].fileToken, //fileToken
                                    filename: docInfo.fileInfo[0].filename,
                                    path: docInfo.fileInfo[0].path,
                                })
                            }

                            if (docInfo?.subtopic?.length > 0) {
                                //@ts-ignore
                                itm.uploadFileObj.subTopicPageList = docInfo.subtopic[0]
                            }
                        }
                    })
                })
            }

            //Filter การซ่อนแสดงหัวข้อเอกสาร ถ้าฝั่งสำนักงานให้ซ่อนหัวข้อเอกสาร แต่ว่าหัวข้อนั้นมีไฟล์อยู่ให้ทำการแสดงหัวข้อนั้นออกมา
            if (uploadFileInit?.length > 0) {
                uploadFileInit = uploadFileInit.filter((itm: any) => {
                    return itm.uploadFileObj.isDisplay || itm.uploadFileObj.fileList.length !== 0
                })
            }
            setInitialUploadFile(uploadFileInit)
        }
    }

    const processSaveData2 = async (
        param: REQServiceTypes.SaveVerifyProjectWithdrawType,
        changeTabOpt?: { idx: number; selectedInfo: any },
    ) => {
        try {
            dialogRef.current?.closeDialog()
            loadingHook.loadingOpen()
            const response = await callWebService.saveVerifyProjectWithdraw(param)

            if (changeTabOpt) {
                if (changeTabOpt.idx !== null && changeTabOpt.selectedInfo !== null) {
                    tabAndFolderHook.setTab(changeTabOpt.idx, changeTabOpt.selectedInfo)
                }
            } else {
                if (onSaveComplete) onSaveComplete(response.status, param.isDraft as boolean)
            }
        } catch (ex) {
            console.log('useVerifyDocumentProjectWithdraw.ts => processSaveData : ', ex)
        } finally {
            loadingHook.loadingClose()
        }
    }
    const onClickCancel = (dirty: boolean) => {
        if (dirty) {
            setConfigDialog({
                title: translate.get('ALERT.TITLE.WARNING'),
                content: translate.get('ALERT.CONTENT.NEED_TO_CANCEL'),
                type: 'alert-confirm',

                onClickConfirm: () => {
                    history.push(RoutePath.manageProject)
                },
            })
            dialogRef.current?.openDialog()
        } else {
            history.push(RoutePath.manageProject)
        }
    }
    const onRefDialog = (func: Record<string, Function>) => {
        //ห้าม set State หรือเรียก Action ในนี้
        dialogRef.current = func
    }

    const onRefForm = (formProps: any) => {
        formRef.current = formProps
    }

    const processSaveData = async () => {
        try {
            const param: REQServiceTypes.SaveConfirmProjectWithdraw = {
                projectID: manageProjectStore.projectData?.projectID ?? null,
                submittedID: manageProjectStore.projectData?.submittedID ?? null,
                isDelete: false,
            }

            dialogRef.current?.closeDialog()
            loadingHook.loadingOpen()
            const response = await callWebService.saveConfirmProjectWithdraw(param)

            if (onSaveComplete) {
                if (response && response.status) {
                    const isDraft: boolean = false
                    onSaveComplete(response.status, isDraft)
                }
            }
        } catch (ex) {
            console.log('useConfirmProjectWithdraw.ts => processSaveData : ', ex)
        } finally {
            loadingHook.loadingClose()
        }
    }

    const onConfirmWithdraw = () => {
        setConfigDialog({
            title: translate.get('ALERT.TITLE.NOTIFICATION'),
            content: translate.get('ALERT.CONTENT.NEED_TO_PROJECT_WITHDRAW'),
            type: 'alert-confirm',
            onClickConfirm: processSaveData,
        })

        dialogRef.current?.openDialog()
    }
    return {
        initialVal: {
            ...initialValues,
            uploadFile: initialUploadFile,
        },
        initialUploadFile,
        uploadDocumentTopic: manageProjectStore.documentTopic,
        isViewMode: isViewMode,
        configDialog,
        onRefForm,
        onClickCancel,
        onRefDialog,
        onConfirmWithdraw,
    }
}
export default useConfirmProjectWithdraw
